import { useState } from 'react';
/**
 * Custom hook for localStorage persistence with type safety and error handling
 */
export function useLocalStorage(key, initialValue) {
    // State to store our value
    const [storedValue, setStoredValue] = useState(() => {
        try {
            // Get from local storage by key
            const item = window.localStorage.getItem(key);
            // Parse stored json or if none return initialValue
            if (item === null) {
                return initialValue;
            }
            return JSON.parse(item);
        }
        catch (error) {
            // If error also return initialValue
            console.warn(`Error loading localStorage key "${key}":`, error);
            return initialValue;
        }
    });
    // Return a wrapped version of useState's setter function that persists the new value to localStorage
    const setValue = (value) => {
        try {
            // Allow value to be a function so we have the same API as useState
            const valueToStore = value instanceof Function ? value(storedValue) : value;
            // Save state
            setStoredValue(valueToStore);
            // Save to local storage
            window.localStorage.setItem(key, JSON.stringify(valueToStore));
        }
        catch (error) {
            // A more advanced implementation would handle the error case
            console.warn(`Error setting localStorage key "${key}":`, error);
        }
    };
    return [storedValue, setValue];
}
/**
 * Clear specific localStorage keys
 */
export function clearLocalStorage(keys) {
    try {
        keys.forEach(key => {
            window.localStorage.removeItem(key);
        });
    }
    catch (error) {
        console.warn('Error clearing localStorage:', error);
    }
}
/**
 * Clear all Multiwise-related localStorage keys
 */
export function clearMultiwiseData() {
    clearLocalStorage([
        'multiwise_current_prompt',
        'multiwise_selected_models',
        'multiwise_session_data'
    ]);
    // Note: We don't clear savings data here as it should persist even after "Clear All"
}
/**
 * Clear all Multiwise data including savings (only used for logout or similar)
 */
export function clearAllMultiwiseData() {
    clearLocalStorage([
        'multiwise_current_prompt',
        'multiwise_selected_models',
        'multiwise_session_data',
        'multiwise_last_savings'
    ]);
}
